/** Workshop hours — Europe/Tallinn (Estonia). */

import { isEmergencyClosedToday } from './emergencyHours';

export const HOURS = {
  timeZone: 'Europe/Tallinn',
  openHour: 9,
  openMinute: 0,
  closeHour: 17,
  closeMinute: 0,
  /** JS getDay(): 0 Sun … 6 Sat — open Mon–Fri */
  openWeekdays: [1, 2, 3, 4, 5] as readonly number[],
  /** Estonian short labels, Mon→Sun */
  dayLabels: ['E', 'T', 'K', 'N', 'R', 'L', 'P'] as const,
  displayRange: '9:00–17:00',
} as const;

export type DayStatus = {
  /** 0 = Monday … 6 = Sunday (display order) */
  index: number;
  label: string;
  /** JS getDay() */
  jsDay: number;
  isOpenDay: boolean;
  isToday: boolean;
};

export type OpeningSnapshot = {
  isOpenNow: boolean;
  isOpenDay: boolean;
  emergencyClosed: boolean;
  /** e.g. "Täna 9:00–17:00" or "Täna suletud" */
  todayLine: string;
  statusLabel: 'Avatud' | 'Suletud';
  /** Accessible full sentence */
  summary: string;
  days: DayStatus[];
};

function partsInTallinn(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: HOURS.timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  });
  const bag = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value]),
  ) as Record<string, string>;

  // weekday short in en-GB: Mon, Tue, …
  const map: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 0,
  };
  const jsDay = map[bag.weekday] ?? date.getDay();
  const hour = Number(bag.hour);
  const minute = Number(bag.minute);
  return { jsDay, hour, minute };
}

export function getOpeningSnapshot(now = new Date()): OpeningSnapshot {
  const { jsDay, hour, minute } = partsInTallinn(now);
  const isOpenDay = HOURS.openWeekdays.includes(jsDay);
  const mins = hour * 60 + minute;
  const openMins = HOURS.openHour * 60 + HOURS.openMinute;
  const closeMins = HOURS.closeHour * 60 + HOURS.closeMinute;
  const emergencyClosed = isEmergencyClosedToday(now);
  const isOpenNow =
    !emergencyClosed && isOpenDay && mins >= openMins && mins < closeMins;

  // Display Mon→Sun — emergency close marks today as closed (dash + grey)
  const days: DayStatus[] = HOURS.dayLabels.map((label, i) => {
    const js = i === 6 ? 0 : i + 1; // E=Mon=1 … P=Sun=0
    const isToday = js === jsDay;
    const normallyOpen = HOURS.openWeekdays.includes(js);
    return {
      index: i,
      label,
      jsDay: js,
      isOpenDay: normallyOpen && !(emergencyClosed && isToday),
      isToday,
    };
  });

  if (emergencyClosed) {
    return {
      isOpenNow: false,
      isOpenDay,
      emergencyClosed: true,
      todayLine: 'Täna suletud',
      statusLabel: 'Suletud',
      summary: `Täna erandkorras suletud. Tavapäraselt tööpäeviti ${HOURS.displayRange}.`,
      days,
    };
  }

  const statusLabel = isOpenNow ? 'Avatud' : 'Suletud';
  const todayLine = isOpenDay
    ? isOpenNow
      ? `Täna ${HOURS.displayRange}`
      : mins < openMins
        ? `Täna avaneb ${HOURS.openHour}:00`
        : `Täna suletud · ${HOURS.displayRange}`
    : 'Täna suletud';

  const summary = isOpenNow
    ? `Praegu avatud. Tööpäeviti ${HOURS.displayRange}.`
    : isOpenDay
      ? `Praegu suletud. Tööpäeviti ${HOURS.displayRange}.`
      : `Nädalavahetusel suletud. Tööpäeviti ${HOURS.displayRange}.`;

  return {
    isOpenNow,
    isOpenDay,
    emergencyClosed: false,
    todayLine,
    statusLabel,
    summary,
    days,
  };
}
